/**
 * Campaign List tab — view, create, pause, activate, and delete campaigns.
 * Also shows ad sets and ads when a campaign is expanded.
 */

import React, { useEffect, useState, useCallback } from 'react'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Spinner,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  SortableHeader,
} from '@/components/ui'
import {
  Plus,
  Pause,
  Play,
  Trash2,
  ChevronDown,
  ChevronRight,
  Search,
  RefreshCw,
  Pencil,
  X,
  Check,
} from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useMarketingStore } from '@/stores/marketingStore'
import { useToast } from '@/components/ui/Toast'
import { CampaignWizard } from './CampaignWizard'
import { ChannelBadge } from '@/components/marketing/ChannelBadge'
import { formatMarketingCents } from '@/components/marketing/format'
import type { Campaign, AdSet, Ad, AdSetUpdateRequest, AdUpdateRequest } from '@/types/marketing'

const formatCurrency = formatMarketingCents

export function CampaignList() {
  const {
    fetchStatus,
    syncCampaigns,
    fetchCampaigns,
    pauseCampaign,
    activateCampaign,
    deleteCampaign,
    fetchAdSets,
    fetchAds,
    updateAdSet,
    updateAd,
    hasAccount,
    campaigns,
  } = useMarketing()
  const { addToast } = useToast()
  const selectedChannel = useMarketingStore((s) => s.selectedChannel)

  const [loading, setLoading] = useState(true)
  const [actionId, setActionId] = useState<string | null>(null)
  const [showWizard, setShowWizard] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [adSetsMap, setAdSetsMap] = useState<Record<string, AdSet[]>>({})
  const [adsMap, setAdsMap] = useState<Record<string, Ad[]>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [sortField, setSortField] = useState<'name' | 'status' | 'objective' | 'dailyBudget'>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [syncing, setSyncing] = useState(false)

  // Inline editing state
  const [editingAdSetId, setEditingAdSetId] = useState<string | null>(null)
  const [editingAdId, setEditingAdId] = useState<string | null>(null)
  const [adSetForm, setAdSetForm] = useState<{ name: string; dailyBudget: string; optimizationGoal: string; status: string }>({ name: '', dailyBudget: '', optimizationGoal: '', status: '' })
  const [adForm, setAdForm] = useState<{ name: string; status: string }>({ name: '', status: '' })
  const [savingAdSet, setSavingAdSet] = useState(false)
  const [savingAd, setSavingAd] = useState(false)

  const handleSync = async () => {
    setSyncing(true)
    try {
      const result = await syncCampaigns()
      await fetchCampaigns()
      addToast({
        title: 'Synced',
        description: `${result.created} imported, ${result.updated} updated`,
        type: 'success',
      })
    } catch {
      const channelLabel = selectedChannel === 'google' ? 'Google Ads' : 'Meta'
      addToast({ title: 'Error', description: `Failed to sync campaigns from ${channelLabel}`, type: 'error' })
    } finally {
      setSyncing(false)
    }
  }

  const loadCampaigns = useCallback(async () => {
    setLoading(true)
    try {
      const status = await fetchStatus()
      if (status.hasAccount) {
        await fetchCampaigns()
      }
    } catch {
      addToast({ title: 'Error', description: 'Failed to load campaigns', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [fetchStatus, fetchCampaigns, addToast])

  // Re-fetch when channel selection changes
  useEffect(() => {
    loadCampaigns()
  }, [loadCampaigns, selectedChannel])

  const handleToggle = async (campaign: Campaign) => {
    setActionId(campaign.id)
    try {
      if (campaign.status === 'ACTIVE') {
        await pauseCampaign(campaign.id)
        addToast({ title: 'Paused', description: `${campaign.name} paused`, type: 'success' })
      } else {
        await activateCampaign(campaign.id)
        addToast({ title: 'Activated', description: `${campaign.name} activated`, type: 'success' })
      }
    } catch {
      addToast({ title: 'Error', description: 'Failed to update campaign status', type: 'error' })
    } finally {
      setActionId(null)
    }
  }

  const handleDelete = async (campaign: Campaign) => {
    setActionId(campaign.id)
    try {
      await deleteCampaign(campaign.id)
      addToast({ title: 'Deleted', description: `${campaign.name} archived`, type: 'success' })
    } catch {
      addToast({ title: 'Error', description: 'Failed to delete campaign', type: 'error' })
    } finally {
      setActionId(null)
    }
  }

  const startEditAdSet = (adSet: AdSet) => {
    setEditingAdSetId(adSet.id)
    setEditingAdId(null)
    setAdSetForm({
      name: adSet.name,
      dailyBudget: adSet.dailyBudget ? (adSet.dailyBudget / 100).toFixed(2) : '',
      optimizationGoal: adSet.optimizationGoal || '',
      status: adSet.status,
    })
  }

  const handleSaveAdSet = async (adSet: AdSet, campaignId: string) => {
    setSavingAdSet(true)
    try {
      const data: AdSetUpdateRequest = { name: adSetForm.name }
      if (adSetForm.dailyBudget) {
        data.dailyBudget = Math.round(parseFloat(adSetForm.dailyBudget) * 100)
      }
      if (adSetForm.optimizationGoal) {
        data.optimizationGoal = adSetForm.optimizationGoal
      }
      if (adSetForm.status !== adSet.status) {
        data.status = adSetForm.status
      }
      await updateAdSet(adSet.id, data)
      // Refresh ad sets for this campaign
      const refreshed = await fetchAdSets(campaignId)
      setAdSetsMap((prev) => ({ ...prev, [campaignId]: refreshed }))
      setEditingAdSetId(null)
      addToast({ title: 'Saved', description: `Ad set "${adSetForm.name}" updated`, type: 'success' })
    } catch {
      addToast({ title: 'Error', description: 'Failed to update ad set', type: 'error' })
    } finally {
      setSavingAdSet(false)
    }
  }

  const startEditAd = (ad: Ad) => {
    setEditingAdId(ad.id)
    setEditingAdSetId(null)
    setAdForm({ name: ad.name, status: ad.status })
  }

  const handleSaveAd = async (ad: Ad, adSetId: string) => {
    setSavingAd(true)
    try {
      const data: AdUpdateRequest = { name: adForm.name }
      if (adForm.status !== ad.status) {
        data.status = adForm.status
      }
      await updateAd(ad.id, data)
      // Refresh ads for this ad set
      const refreshed = await fetchAds(adSetId)
      setAdsMap((prev) => ({ ...prev, [adSetId]: refreshed }))
      setEditingAdId(null)
      addToast({ title: 'Saved', description: `Ad "${adForm.name}" updated`, type: 'success' })
    } catch {
      addToast({ title: 'Error', description: 'Failed to update ad', type: 'error' })
    } finally {
      setSavingAd(false)
    }
  }

  const handleExpand = async (campaignId: string) => {
    if (expandedId === campaignId) {
      setExpandedId(null)
      return
    }
    setExpandedId(campaignId)
    if (!adSetsMap[campaignId]) {
      try {
        const adSets = await fetchAdSets(campaignId)
        setAdSetsMap((prev) => ({ ...prev, [campaignId]: adSets }))
        // Load ads for each ad set
        for (const adSet of adSets) {
          const ads = await fetchAds(adSet.id)
          setAdsMap((prev) => ({ ...prev, [adSet.id]: ads }))
        }
      } catch {
        // silently handle
      }
    }
  }

  const filtered = campaigns
    .filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((c) => statusFilter === 'ALL' || c.status === statusFilter)
    .sort((a, b) => {
      let cmp = 0
      if (sortField === 'name') cmp = a.name.localeCompare(b.name)
      else if (sortField === 'status') cmp = (a.status || '').localeCompare(b.status || '')
      else if (sortField === 'objective') cmp = (a.objective || '').localeCompare(b.objective || '')
      else if (sortField === 'dailyBudget') cmp = (a.dailyBudget || 0) - (b.dailyBudget || 0)
      return sortDir === 'asc' ? cmp : -cmp
    })

  const handleSort = (by: string, order: 'asc' | 'desc') => {
    setSortField(by as typeof sortField)
    setSortDir(order)
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!hasAccount) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No ad account connected. Go to <strong>Settings</strong> to connect.
        </CardContent>
      </Card>
    )
  }

  if (showWizard) {
    return (
      <CampaignWizard
        onClose={() => setShowWizard(false)}
        onCreated={() => {
          setShowWizard(false)
          loadCampaigns()
        }}
      />
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9 text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="PAUSED">Paused</SelectItem>
              <SelectItem value="ARCHIVED">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={handleSync} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : `Sync from ${selectedChannel === 'google' ? 'Google Ads' : 'Meta'}`}
          </Button>
          <Button onClick={() => setShowWizard(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Campaign list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {campaigns.length === 0 ? 'No campaigns yet. Create your first campaign.' : 'No campaigns match your search.'}
          </CardContent>
        </Card>
      ) : (
        <Card>
            <Table containerClassName="max-h-[65vh] overflow-y-auto">
              <TableHeader className="z-10">
                <TableRow>
                  <TableHead className="w-8" />
                  <SortableHeader label="Campaign" sortKey="name" currentSortBy={sortField} currentSortOrder={sortDir} onSort={handleSort} />
                  <SortableHeader label="Status" sortKey="status" currentSortBy={sortField} currentSortOrder={sortDir} onSort={handleSort} />
                  <SortableHeader label="Objective" sortKey="objective" currentSortBy={sortField} currentSortOrder={sortDir} onSort={handleSort} />
                  <SortableHeader label="Budget" sortKey="dailyBudget" currentSortBy={sortField} currentSortOrder={sortDir} onSort={handleSort} align="right" />
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
          {filtered.map((campaign) => (
            <React.Fragment key={campaign.id}>
            <TableRow className="group">
              <td className="px-4 py-2">
                    <button
                      onClick={() => handleExpand(campaign.id)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {expandedId === campaign.id ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
              </td>
              <td className="px-4 py-2">
                    <p className="font-medium truncate flex items-center gap-1.5">
                        <ChannelBadge channel={campaign.channel} showLabel={false} />
                        {campaign.name}
                    </p>
              </td>
              <td className="px-4 py-2">
                    <StatusBadge status={campaign.status} />
              </td>
              <td className="px-4 py-2 text-muted-foreground">
                    {campaign.objective || '—'}
              </td>
              <td className="px-4 py-2 text-right text-muted-foreground">
                    {campaign.dailyBudget ? `${formatCurrency(campaign.dailyBudget)}/day` : '—'}
              </td>
              <td className="px-4 py-2 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(campaign)}
                      disabled={actionId === campaign.id}
                      title={campaign.status === 'ACTIVE' ? 'Pause' : 'Activate'}
                    >
                      {actionId === campaign.id ? (
                        <Spinner size="sm" />
                      ) : campaign.status === 'ACTIVE' ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(campaign)}
                      disabled={actionId === campaign.id}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
              </td>

            </TableRow>
                {/* Expanded: Ad Sets + Ads */}
                {expandedId === campaign.id && (
              <tr><td colSpan={6} className="px-4 py-3 bg-muted/50">
                  <div className="ml-7 border-l-2 border-border pl-4 space-y-3">
                    {(adSetsMap[campaign.id] || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">No ad sets</p>
                    ) : (
                      (adSetsMap[campaign.id] || []).map((adSet) => (
                        <div key={adSet.id} className="space-y-1">
                          {editingAdSetId === adSet.id ? (
                            /* ── Ad Set Inline Editor ── */
                            <div className="space-y-2 bg-muted rounded-md p-3">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Name</label>
                                  <Input
                                    value={adSetForm.name}
                                    onChange={(e) => setAdSetForm((f) => ({ ...f, name: e.target.value }))}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Daily Budget ($)</label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={adSetForm.dailyBudget}
                                    onChange={(e) => setAdSetForm((f) => ({ ...f, dailyBudget: e.target.value }))}
                                    placeholder="0.00"
                                    className="text-sm"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Optimization Goal</label>
                                  <Select
                                    value={adSetForm.optimizationGoal}
                                    onValueChange={(v) => setAdSetForm((f) => ({ ...f, optimizationGoal: v }))}
                                  >
                                    <SelectTrigger className="text-sm">
                                      <SelectValue placeholder="Select goal" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="CONVERSATIONS">Conversations (CTWA)</SelectItem>
                                      <SelectItem value="LINK_CLICKS">Link Clicks</SelectItem>
                                      <SelectItem value="IMPRESSIONS">Impressions</SelectItem>
                                      <SelectItem value="REACH">Reach</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                                  <Button
                                    variant={adSetForm.status === 'ACTIVE' ? 'default' : 'secondary'}
                                    size="sm"
                                    className="w-full"
                                    onClick={() => setAdSetForm((f) => ({ ...f, status: f.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' }))}
                                  >
                                    {adSetForm.status === 'ACTIVE' ? (
                                      <><Play className="w-3 h-3 mr-1" /> Active</>
                                    ) : (
                                      <><Pause className="w-3 h-3 mr-1" /> Paused</>
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 pt-1">
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveAdSet(adSet, campaign.id)}
                                  disabled={savingAdSet}
                                >
                                  {savingAdSet ? (
                                    <Spinner className="w-3 h-3 mr-1" />
                                  ) : (
                                    <Check className="w-3 h-3 mr-1" />
                                  )}
                                  Save
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingAdSetId(null)}
                                  disabled={savingAdSet}
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* ── Ad Set Read-Only ── */
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div>
                                  <p className="text-sm font-medium">{adSet.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {adSet.optimizationGoal || 'No goal'}
                                    {adSet.dailyBudget ? ` · ${formatCurrency(adSet.dailyBudget)}/day` : ''}
                                  </p>
                                </div>
                                <button
                                  onClick={() => startEditAdSet(adSet)}
                                  className="text-muted-foreground hover:text-foreground"
                                  title="Edit ad set"
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                              </div>
                              <StatusBadge status={adSet.status} />
                            </div>
                          )}
                          {/* Ads under this ad set */}
                          {(adsMap[adSet.id] || []).map((ad) => (
                            <div key={ad.id} className="ml-4 border-l border-border pl-3 py-1">
                              {editingAdId === ad.id ? (
                                /* ── Ad Inline Editor ── */
                                <div className="space-y-2 bg-muted rounded-md p-2">
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Name</label>
                                      <Input
                                        value={adForm.name}
                                        onChange={(e) => setAdForm((f) => ({ ...f, name: e.target.value }))}
                                        className="text-xs"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-muted-foreground mb-1 block">Status</label>
                                      <Button
                                        variant={adForm.status === 'ACTIVE' ? 'default' : 'secondary'}
                                        size="sm"
                                        className="w-full"
                                        onClick={() => setAdForm((f) => ({ ...f, status: f.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' }))}
                                      >
                                        {adForm.status === 'ACTIVE' ? (
                                          <><Play className="w-3 h-3 mr-1" /> Active</>
                                        ) : (
                                          <><Pause className="w-3 h-3 mr-1" /> Paused</>
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 pt-1">
                                    <Button
                                      size="sm"
                                      onClick={() => handleSaveAd(ad, adSet.id)}
                                      disabled={savingAd}
                                    >
                                      {savingAd ? (
                                        <Spinner className="w-3 h-3 mr-1" />
                                      ) : (
                                        <Check className="w-3 h-3 mr-1" />
                                      )}
                                      Save
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setEditingAdId(null)}
                                      disabled={savingAd}
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                /* ── Ad Read-Only ── */
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div>
                                      <p className="text-xs font-medium">{ad.name}</p>
                                      {ad.approvalStatus && (
                                        <p className="text-xs text-muted-foreground">
                                          Approval: {ad.approvalStatus}
                                          {ad.rejectionReason && ` (${ad.rejectionReason})`}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => startEditAd(ad)}
                                      className="text-muted-foreground hover:text-foreground"
                                      title="Edit ad"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <StatusBadge status={ad.status} />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
              </td></tr>
                )}
            </React.Fragment>
          ))}
              </TableBody>
            </Table>
        </Card>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === 'ACTIVE' ? 'success' as const :
    status === 'PAUSED' ? 'secondary' as const :
    status === 'DELETED' ? 'destructive' as const :
    'outline' as const

  return <Badge variant={variant}>{status}</Badge>
}
